import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { getJournalStats } from "../services/journalService";
import formatDate from "./utils/formatDate";
import Loader from "./common/loader";
import ContributionGraph from "./contributionGraph";

const StatBox = styled.div`
  display: block;
  background-color: ${({ theme }) => theme.card};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 1rem;
  color: inherit;
  text-decoration: none;

  @media only screen and (max-width: 600px) {
    padding: 1rem;
  }
`;

const StatNumber = styled.div`
  font-size: ${({ small }) => (small ? "1.5rem" : "2.5rem")};
  font-weight: bold;
  overflow-wrap: break-word;

  @media only screen and (max-width: 600px) {
    font-size: ${({ small }) => (small ? "1.1rem" : "1.8rem")};
  }
`;

const isNumeric = value => /^-?[\d.,%]+$/.test(String(value));

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.7;

  @media only screen and (max-width: 600px) {
    font-size: 0.75rem;
  }
`;

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// dayStr is a "YYYY-MM-DD" key (no time component) - format it without
// going through Date parsing, which would shift it by the local timezone.
const formatDay = dayStr => {
  const [year, month, day] = dayStr.split("-").map(Number);
  return `${day} ${monthNames[month - 1]} ${year}`;
};

const formatDuration = totalDays => {
  const years = Math.floor(totalDays / 365.25);
  const days = Math.round(totalDays - years * 365.25);
  if (years === 0) return `${days} day${days === 1 ? "" : "s"}`;
  return `${years} year${years === 1 ? "" : "s"}, ${days} day${
    days === 1 ? "" : "s"
  }`;
};

const YearBarRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.4rem;
`;

const YearLabel = styled.div`
  width: 3.5rem;
  font-weight: bold;
`;

const YearBarTrack = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.body};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: 8px;
  height: 1.2rem;
  overflow: hidden;
  margin: 0 0.5rem;
`;

const YearBarFill = styled.div`
  background: ${({ theme }) => theme.paginationBtn};
  height: 100%;
`;

const YearCount = styled.div`
  width: 2rem;
  text-align: right;
`;

const Stats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      const { data } = await getJournalStats();
      setStats(data);
      setLoading(false);
    }
    fetchStats();
  }, []);

  if (loading) return <Loader fontSize="4rem" />;

  const maxYearCount = stats.entriesPerYear
    ? Math.max(...stats.entriesPerYear.map(y => y.count))
    : 0;

  const items = [
    { label: "Total entries", value: stats.totalEntries },
    { label: "Current streak (days)", value: stats.currentStreak },
    {
      label: stats.longestStreakStart
        ? `Longest streak: ${formatDay(stats.longestStreakStart)} → ${formatDay(
            stats.longestStreakEnd
          )}`
        : "Longest streak (days)",
      value: stats.longestStreak,
    },
    {
      label: stats.longestGapStart
        ? `Longest gap: ${formatDay(stats.longestGapStart)} → ${formatDay(
            stats.longestGapEnd
          )}`
        : "Longest gap (days)",
      value: stats.longestGapDays,
    },
    { label: "Writing consistency", value: `${stats.consistencyRate}%` },
    { label: `Starred (${stats.starredPercent}%)`, value: stats.starredCount },
    { label: "Locked entries", value: stats.lockedCount },
    { label: "Total words written", value: stats.totalWords },
    { label: "Average words / entry", value: stats.averageWords },
    { label: "Most active day", value: stats.mostActiveWeekday || "—" },
    { label: "Least active day", value: stats.leastActiveWeekday || "—" },
    { label: "Most active time", value: stats.mostActiveTimeOfDay || "—" },
    { label: "Entries this month", value: stats.entriesThisMonth },
    { label: "Entries this year", value: stats.entriesThisYear },
    { label: "Avg entries / week", value: stats.avgEntriesPerWeek },
    {
      label: stats.busiestMonth
        ? `Busiest month: ${monthNames[stats.busiestMonth.month]} ${
            stats.busiestMonth.year
          }`
        : "Busiest month ever",
      value: stats.busiestMonth ? stats.busiestMonth.count : "—",
    },
  ];

  return (
    <Container className="mt-2">
      <h4 className="mb-1">Your journaling stats</h4>
      {stats.firstEntryDate && (
        <p className="text-muted small mb-3">
          Journaling since {formatDate(new Date(stats.firstEntryDate))}
          {stats.daysSinceFirstEntry > 0 &&
            ` · ${formatDuration(stats.daysSinceFirstEntry)}`}
        </p>
      )}
      <ContributionGraph
        dailyCounts={stats.dailyCounts}
        entriesPerYear={stats.entriesPerYear}
      />
      <Row>
        {items.map(item => (
          <Col xs={6} md={4} key={item.label}>
            <StatBox>
              <StatNumber small={!isNumeric(item.value)}>
                {item.value}
              </StatNumber>
              <StatLabel>{item.label}</StatLabel>
            </StatBox>
          </Col>
        ))}
        {stats.longestEntry && (
          <Col xs={6} md={4}>
            <StatBox as={Link} to={`/journals/${stats.longestEntry.id}`}>
              <StatNumber>{stats.longestEntry.words}</StatNumber>
              <StatLabel>
                Longest entry (words) · {formatDate(new Date(stats.longestEntry.date))}
              </StatLabel>
            </StatBox>
          </Col>
        )}
      </Row>
      {stats.entriesPerYear && stats.entriesPerYear.length > 0 && (
        <div className="mt-2 mb-4">
          <h5 className="mb-2">Entries per year</h5>
          {stats.entriesPerYear.map(({ year, count }) => (
            <YearBarRow key={year}>
              <YearLabel>{year}</YearLabel>
              <YearBarTrack>
                <YearBarFill
                  style={{ width: `${(count / maxYearCount) * 100}%` }}
                />
              </YearBarTrack>
              <YearCount>{count}</YearCount>
            </YearBarRow>
          ))}
        </div>
      )}
    </Container>
  );
};

export default Stats;
